<template>
  <div class="informe-uso-container">
    <CardPrime class="informe-uso-card">
      <template #title>
        <div class="card-header">
          <h3 class="card-title">Informe de Uso</h3>
        </div>
      </template>
      <template #content>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div class="form-field">
            <label for="colegio-select" class="block text-sm font-medium text-gray-700">Seleccionar Colegio:</label>
            <ColegioDropdown
              v-model="selectedColegio"
              :mostrarErrores="mostrarErrores"
              :tooltip="'Selecciona un Colegio'"
              :error="errors.colegio"
            />
            <small v-if="mostrarErrores && errors.colegio" class="p-error">{{ errors.colegio }}</small>
          </div>

          <div class="form-field">
            <label for="curso-select" class="block text-sm font-medium text-gray-700">Seleccionar Curso:</label>
            <SingleCursoCombo
              :mostrarErrores="mostrarErrores"
              :tooltip="'Selecciona un Curso'"
              :error="errors.curso"
              :modelValue="selectedCurso"
              @update:modelValue="selectedCurso = $event"
            />
            <small v-if="mostrarErrores && errors.curso" class="p-error">{{ errors.curso }}</small>
          </div>

          <div class="form-field">
            <label for="inicio" class="block text-sm font-medium text-gray-700">Fecha Inicio:</label>
            <input
              type="date"
              id="inicio"
              v-model="inicio"
              class="p-inputtext w-full border-gray-300 rounded-md shadow-sm"
            />
          </div>

          <div class="form-field">
            <label for="fin" class="block text-sm font-medium text-gray-700">Fecha Fin:</label>
            <input
              type="date"
              id="fin"
              v-model="fin"
              class="p-inputtext w-full border-gray-300 rounded-md shadow-sm"
            />
          </div>
        </div>

        <ButtonPrime label="Generar Informe" @click="generarInforme" class="mb-4" :disabled="loading" />

        <div v-if="informe.length > 0" class="mt-4">
          <h4 class="text-lg font-semibold mb-2">Informe de Uso:</h4>
          <DataTable
            :value="informe"
            :paginator="true"
            :rows="rows"
            :totalRecords="totalRecords"
            :lazy="true"
            :loading="loading"
            @page="onPage"
            paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown CurrentPageReport"
            :rowsPerPageOptions="[10, 20, 50, 150, 200, 250]"
            currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords} registros"
            responsiveLayout="scroll"
            class="p-datatable-sm"
            dataKey="IdUsuario"
          >
            <ColumnPrime field="IdUsuario" header="ID Usuario" sortable />
            <ColumnPrime field="Email" header="Email" sortable />
            <ColumnPrime field="NombreCompleto" header="Nombre Completo" sortable>
              <template #body="slotProps">
                {{ slotProps.data.Nombre }} {{ slotProps.data.ApellidoPaterno }} {{ slotProps.data.ApellidoMaterno }}
              </template>
            </ColumnPrime>
            <ColumnPrime field="Casa" header="Casa" sortable />
            <ColumnPrime field="TotalConexiones" header="Conexiones" sortable />
            <ColumnPrime field="TiempoTotalSegundos" header="Tiempo (H:M:S)" sortable>
              <template #body="slotProps">
                {{ formatTiempo(slotProps.data.TiempoTotalSegundos) }}
              </template>
            </ColumnPrime>
            <ColumnPrime field="CursosDistintos" header="Cursos Distintos" sortable />
            <ColumnPrime field="AsignaturasDistintas" header="Asignaturas" sortable />
            <ColumnPrime field="UltimaConexion" header="Última Conexión" sortable>
              <template #body="slotProps">
                {{ new Date(slotProps.data.UltimaConexion).toLocaleString('es-CL') }}
              </template>
            </ColumnPrime>
          </DataTable>
        </div>
        <div v-else-if="informe.length === 0 && !loading && wasGenerated" class="mt-4 p-4 bg-yellow-100 text-yellow-700 rounded">
          No se encontraron datos para los filtros seleccionados.
        </div>

        <ToastPrime />
      </template>
    </CardPrime>
  </div>
</template>

<script>
import usuarioService from '@/services/UsuarioService';
import CardPrime from 'primevue/card';
import ButtonPrime from 'primevue/button';
import DataTable from 'primevue/datatable';
import ColumnPrime from 'primevue/column';
import ToastPrime from 'primevue/toast';
import ColegioDropdown from '@/components/selectores/ColegioDropdown.vue';
import SingleCursoCombo from '@/components/selectores/SingleCursoCombo.vue';

export default {
  components: {
    CardPrime,
    ButtonPrime,
    DataTable,
    ColumnPrime,
    ToastPrime,
    ColegioDropdown,
    SingleCursoCombo
  },
  data() {
    return {
      selectedColegio: '',
      selectedCurso: '',
      inicio: '2025-05-01',
      fin: '2025-05-31',
      informe: [],
      rows: 10,
      totalRecords: 0,
      currentPage: 1,
      loading: false,
      wasGenerated: false,
      errors: {
        colegio: '',
        curso: ''
      },
      mostrarErrores: false
    };
  },
  watch: {
    selectedColegio() {
      this.cargarCursos();
    },
  },
  methods: {
    async cargarCursos() {
      if (!this.selectedColegio) {
        this.cursos = [];
        this.selectedCurso = null;
        return;
      }
      try {
        const response = await usuarioService.getCursosPorColegio(this.selectedColegio);
        this.cursos = response.data;
        this.selectedCurso = null;
      } catch (error) {
        console.error('Error al cargar cursos:', error);
        this.$toast.add({
          severity: 'error',
          summary: 'Error',
          detail: 'No se pudieron cargar los cursos.',
          life: 3000
        });
      }
    },
    async generarInforme() {
      this.mostrarErrores = false;
      this.wasGenerated = true;

      if (!this.selectedColegio) {
        this.errors.colegio = 'Debes seleccionar un colegio';
        this.mostrarErrores = true;
      }
      if (!this.selectedCurso) {
        this.errors.curso = 'Debes seleccionar un curso';
        this.mostrarErrores = true;
      }
      if (!this.inicio || !this.fin || new Date(this.inicio) > new Date(this.fin)) {
        this.$toast.add({
          severity: 'warn',
          summary: 'Advertencia',
          detail: 'Fechas inválidas o incompletas.',
          life: 3000
        });
        return;
      }
      if (this.mostrarErrores) return;

      this.loading = true;
      try {
        const response = await usuarioService.getUsoUsuario(
          this.inicio,
          this.fin,
          this.selectedColegio,
          this.selectedCurso
        );


        if (!response || !response.data.Data || !Array.isArray(response.data.Data)) {
          throw new Error('Respuesta inválida del servidor: datos no disponibles.');
        }

        this.informe = response.data.Data.map(item => ({
          ...item,
          NombreCompleto: `${item.Nombre} ${item.ApellidoPaterno} ${item.ApellidoMaterno}`
        }));
        this.totalRecords = this.informe.length;

      } catch (error) {
        console.error('Error al generar informe:', error);
        this.informe = [];
        this.totalRecords = 0;
        alert( error.message || 'No se pudo generar el informe.')

      } finally {
        this.loading = false;
      }
    },
    onPage(event) {
      this.currentPage = event.page + 1;
      this.rows = event.rows;
      this.generarInforme();
    },
    formatTiempo(segundos) {
      if (!segundos) return '00:00:00';
      const horas = Math.floor(segundos / 3600);
      const minutos = Math.floor((segundos % 3600) / 60);
      const secs = segundos % 60;
      return `${String(horas).padStart(2, '0')}:${String(minutos).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
    }
  },
  mounted() {
    // Cargar colegios si ColegioDropdown no lo hace automáticamente
  }
};
</script>

<style scoped>
.informe-uso-container {
  @apply p-4;
}

.informe-uso-card {
  @apply bg-white rounded-lg shadow p-4;
}

.card-header {
  @apply flex justify-between items-center mb-4;
}

.card-title {
  @apply text-xl font-bold text-gray-800;
}

.form-field {
  @apply mb-4;
}

.form-field label {
  @apply block text-sm font-medium text-gray-700 mb-1;
}

.p-inputtext {
  @apply border-gray-300 rounded-md shadow-sm;
}

:deep(.p-datatable-sm) .p-datatable-thead > tr > th,
:deep(.p-datatable-sm) .p-datatable-tbody > tr > td {
  @apply p-2 text-sm;
}
</style>